import { createInstance, TFunction } from "i18next";

import enGB from "../locales/en-GB.json";
import { DEFAULT_LANGUAGE_CODE } from "../utils/languageUtils";

/**
 * EN-5191: English copy for strings that are **stored**, not displayed.
 *
 * A trigger's summary sentence goes to the API as `hcpMessage` and is read back by every other
 * user and by the alert notification, so it has to read the same whoever wrote it. The first cut
 * used `i18n.getFixedT("en-GB")`, which looks the right thing to do and is not: it reads the
 * live store, and three things rewrite that store.
 *
 *  - `?i18nDebug=1` wraps every API-served value in ⟦…⟧ before adding it to the en-GB bundle.
 *  - `?pseudo=1` pseudo-localises the en-GB baseline at init.
 *  - the published en-GB bundle is layered over the shipped one at runtime — and a session
 *    running in another language never fetches it, so the same trigger would be stored with
 *    different wording depending on the author's language.
 *
 * Any of those would have been persisted verbatim and shown to everyone else.
 *
 * This instance is deliberately isolated: its own i18next, the imported JSON and nothing else,
 * no detector, no API overlay, no debug transform. The copy it returns is therefore frozen at
 * the build — which is the point. A stored sentence is a record of what the author wrote, and
 * stability matters more than picking up a later edit in /admin/translations. If that ever needs
 * to change, it is a deliberate decision to make here, not a side effect of how `t` resolves.
 */
const persistedCopy = createInstance();

persistedCopy.init({
  lng: DEFAULT_LANGUAGE_CODE,
  fallbackLng: DEFAULT_LANGUAGE_CODE,
  // A copy: nothing should be able to mutate the imported module through this instance.
  resources: { [DEFAULT_LANGUAGE_CODE]: { translation: { ...enGB } } },
  // Same as the main instance: the keys are flat, so a dot must not split them.
  keySeparator: false,
  interpolation: { escapeValue: false },
  // Resources are inline, so there is nothing to wait for — initialise synchronously, or the
  // first caller gets its key back instead of the sentence.
  initImmediate: false,
});

/** `t` for copy that will be stored. Never use it for anything on screen. */
export const persistedCopyT: TFunction = persistedCopy.t.bind(
  persistedCopy
) as TFunction;
